import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import {
  AppBar,
  Badge,
  Box,
  Fade,
  Grid,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import aktivu from '../assets/images/aktivu-b.png';
import {
  logOut,
  selectUserOrganization,
} from '../components/core/auth/authSlice';
import { selectPerms } from '../components/core/auth/authSlice';
import Nav from '../components/nav/Nav';
import { openSnackbar } from '../components/shared/AktivuSnackbar/snackSlice';
import DynamicIcon from '../components/shared/DynamicIcon';
import { FULL_HEIGHT } from '../utils/constants';
import { deleteCookies } from '../utils/deleteCookies';
import { decodeAndCheckToken } from '../utils/utils';

/**
 * Landing component
 * @returns
 */
function Landing() {
  const [cookies] = useCookies(['']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const perms = useSelector(selectPerms);
  const [toggleMenu, setToggleMenu] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const orgId = useSelector(selectUserOrganization);

  const [deviceMenu, setDeviceMenu] = useState([]);

  const defaultMenu = [
    {
      title: 'Profile Settings',
      url: '/aktivu/profilesettings',
      id: 1,
    },
    {
      title: 'Change Password',
      url: '/aktivu/changepassword',
      id: 2,
    },
  ];

  /**
   * doLogout() - clear cookies and bounce the user
   */
  const doLogout = () => {
    deleteCookies();
    dispatch(logOut());
    navigate('/');
  };

  const getUserAuthAfterLogin = () => {
    let jwtValid = false;
    const clearInactiveSession = setInterval(() => {
      if (cookies?.token) {
        const token = cookies?.token;
        jwtValid = decodeAndCheckToken(token);
      }
      if (!jwtValid) {
        clearInterval(clearInactiveSession);
        dispatch(
          openSnackbar({
            severity: 'error',
            message: 'Session has expire!',
          })
        );
        doLogout();
      }
    }, 1000);
  };

  useEffect(() => {
    getUserAuthAfterLogin();
    if (orgId > 1) {
      setDeviceMenu([
        ...defaultMenu,
        {
          title: 'Configure Device Key',
          url: '/aktivu/devicekey',
          id: 3,
        },
      ]);
    } else {
      setDeviceMenu([...defaultMenu]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  let roleName = perms?.RN ?? 'user';

  if (roleName === 'department_chief') {
    roleName = 'Department Chief';
  } else if (roleName === 'read_only') {
    roleName = 'Read Only User';
  } else if (roleName === 'superuser') {
    roleName = 'Pycube Admin';
  } else if (roleName === 'technician') {
    roleName = 'Technician';
  } else if (roleName === 'admin') {
    roleName = 'Organization Admin';
  } else if (roleName === 'pycube_admin') {
    roleName = 'Pycube Admin';
  } else if (roleName === 'csm') {
    roleName = 'Csm';
  }

  const theme = useTheme();

  /**
   * TODO: Placeholder to show settings panel
   */
  const showSettings = event => {
    console.info(`Placeholder: UI for user settings!`);
    setAnchorEl(event.currentTarget);
    setToggleMenu(!toggleMenu);
  };

  const showAlerts = () => {
    console.info(`Placeholder: UI to display important alerts!`);
  };

  /*Here to render Full menu for Larger screens*/
  const FullMenu = (
    <Grid item sx={{ backgroundColor: theme.palette.dark.main }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Nav menu="fullMenu" />
      </Box>
    </Grid>
  );

  /*Here to render Icon menu for small screens*/
  const IconMenu = (
    <Grid item sx={{ backgroundColor: theme.palette.dark.main }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Nav menu="iconMenu" />
      </Box>
    </Grid>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box component="img" sx={{ height: 64 }} src={aktivu} />
          <Typography
            variant="subtitle1"
            sx={{
              marginLeft: 2,
              flexGrow: 1,
              color: theme.palette.grey_500.main,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            your asset management portal
          </Typography>
          {cookies?.email && (
            <Typography
              sx={{
                display: {
                  xs: 'none',
                  md: 'block',
                  textAlign: 'center',
                },
              }}
            >
              {cookies.email} <br />
              <strong>{roleName}</strong>
            </Typography>
          )}
          <IconButton
            size="large"
            color="inherit"
            id="composition-button"
            aria-controls={
              toggleMenu ? 'composition-menu' : undefined
            }
            aria-expanded={toggleMenu ? 'true' : undefined}
            aria-haspopup="true"
            onClick={showSettings}
          >
            <DynamicIcon iconName="AccountCircle" />
          </IconButton>
          <Popper
            open={toggleMenu}
            anchorEl={anchorEl}
            placement="bottom-end"
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper>
                  <MenuList>
                    {deviceMenu.map(item => (
                      <MenuItem
                        key={item.id}
                        onClick={() => navigate(item.url)}
                      >
                        {item.title}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Paper>
              </Fade>
            )}
          </Popper>
          {false && (
            <IconButton
              size="large"
              color="inherit"
              onClick={showAlerts}
            >
              <Badge badgeContent={4} color="error">
                <NotificationImportantIcon color="inherit" />
              </Badge>
            </IconButton>
          )}
          <IconButton size="large" color="inherit" onClick={doLogout}>
            <DynamicIcon iconName="Logout" />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Grid container spacing={0}>
        <Grid
          sx={{ display: { xs: 'none', md: 'block' } }}
          item
          md={2}
        >
          {FullMenu}
        </Grid>
        <Grid
          sx={{ display: { xs: 'block', md: 'none' } }}
          item
          xs={1}
        >
          {IconMenu}
        </Grid>
        <Grid item xs={11} md={10}>
          <Grid
            item
            sx={{ height: FULL_HEIGHT }}
            style={{ backgroundColor: theme.palette.white.main }}
          >
            <Outlet />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default Landing;
